if (!customElements.get("video-background")) {
  customElements.define(
    "video-background",
    class VideoBackground extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.intersecting = false;
        this.videoLoaded = false;
        this.ytPlayer = null;
        this.vimeoPlayer = null;
        this.isPlaying = false;
      }

      connectedCallback() {
        this.videoType = this.dataset.type;
        this.videoId = this.dataset.videoId;
        this.sectionId = this.dataset.sectionId;
        this.videoElement = this.querySelector(`#${this.sectionId}-video-player`);
        this.videoLoaderElement = this.querySelector(".video-background-loader");
        this.videoPlayerWrapper = this.querySelector(".video-background-player");

        /* Find the section wrapper for overlay/button/thumbnail control */
        this.sectionWrap = this.closest(".video-background-section-wrap-inner");
        this.playBtn = this.sectionWrap
          ? this.sectionWrap.querySelector(".video-background-play-btn")
          : null;
        this.backdrop = this.sectionWrap
          ? this.sectionWrap.querySelector(".video-background-section-backdrop")
          : null;

        /* Bind play button */
        if (this.playBtn) {
          this.playBtn.addEventListener("click", () => this.play());
        }

        /* Preload video sources on intersection but do NOT autoplay */
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.intersecting) {
                this.intersecting = true;
                this.prepareVideo();
                observer.unobserve(this);
              }
            });
          },
          { rootMargin: "200px" },
        );

        this.observer.observe(this);
      }

      /** Load video resources without playing */
      prepareVideo() {
        switch (this.videoType) {
          case "self-hosted":
            this.prepareSelfHosted();
            break;
          case "youtube":
            VideoBackground.loadYouTube();
            this.prepareYouTube();
            break;
          case "vimeo":
            VideoBackground.loadVimeo();
            this.prepareVimeo();
            break;
        }
      }

      /* ---------- Self-hosted ---------- */
      prepareSelfHosted() {
        const player = this.querySelector("video");
        if (!player) return;

        /* Remove loop and muted attrs — user-triggered playback */
        player.removeAttribute("loop");
        player.muted = false;

        if (!this.videoLoaded) {
          this.loadVideoSources(player);
        }

        player.addEventListener("ended", () => this.onVideoEnded());

        /* When user pauses via native controls, restore thumbnail + play button */
        player.addEventListener("pause", () => {
          if (player.ended) return; /* ended handler takes care of this */
          this.onVideoPaused();
        });
      }

      loadVideoSources(player) {
        const sources = player.querySelectorAll("source[data-src]");
        sources.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute("data-src");
        });
        player.load();
        this.videoLoaded = true;
      }

      /* ---------- YouTube ---------- */
      prepareYouTube() {
        if (typeof YT === "undefined" || typeof YT.Player === "undefined") {
          return setTimeout(() => this.prepareYouTube(), 333);
        }

        this.ytPlayer = new YT.Player(`${this.sectionId}-video-player`, {
          videoId: this.videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            showinfo: 0,
            modestbranding: 1,
            loop: 0,
            fs: 0,
            mute: 0,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: () => {
              /* Player is ready but don't play yet */
              this.videoLoaderElement.classList.add("is-hidden");
              this.setAttribute("data-loaded", "true");
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.showPlaying();
              }
              if (event.data === YT.PlayerState.ENDED) {
                this.onVideoEnded();
              }
            },
          },
        });
      }

      /* ---------- Vimeo ---------- */
      prepareVimeo() {
        if (typeof Vimeo === "undefined" || typeof Vimeo.Player === "undefined") {
          return setTimeout(() => this.prepareVimeo(), 333);
        }

        this.vimeoPlayer = new Vimeo.Player(this.videoElement, {
          id: this.videoId,
          loop: false,
          autoplay: false,
          byline: false,
          title: false,
          muted: false,
          playsinline: true,
          controls: true,
        });

        this.vimeoPlayer.ready().then(() => {
          this.videoLoaderElement.classList.add("is-hidden");
          this.setAttribute("data-loaded", "true");
        });

        this.vimeoPlayer.on("play", () => this.showPlaying());
        this.vimeoPlayer.on("ended", () => this.onVideoEnded());
      }

      /* ---------- Playback control ---------- */

      play() {
        if (this.isPlaying) return;

        switch (this.videoType) {
          case "self-hosted": {
            const video = this.querySelector("video");
            if (video) {
              video.controls = true;
              video.play().catch((e) => console.warn("Play failed:", e));
            }
            break;
          }
          case "youtube":
            if (this.ytPlayer && this.ytPlayer.playVideo) {
              this.ytPlayer.playVideo();
            }
            break;
          case "vimeo":
            if (this.vimeoPlayer) {
              this.vimeoPlayer.play();
            }
            break;
        }

        this.showPlaying();
      }

      /** Show the video, hide overlay + button + thumbnail */
      showPlaying() {
        this.isPlaying = true;
        this.videoPlayerWrapper.classList.add("visible");
        this.videoLoaderElement.classList.add("is-hidden");
        this.setAttribute("data-loaded", "true");

        /* Hide overlay, play button, and thumbnail */
        if (this.sectionWrap) {
          this.sectionWrap.classList.add("video-is-playing");
        }
      }

      /** User paused via native controls — show thumbnail + play button but keep position */
      onVideoPaused() {
        this.isPlaying = false;

        const video = this.querySelector("video");
        if (video) video.controls = false;

        this.videoPlayerWrapper.classList.remove("visible");

        if (this.sectionWrap) {
          this.sectionWrap.classList.remove("video-is-playing");
        }
      }

      /** Video ended — show overlay + button + thumbnail, reset to start */
      onVideoEnded() {
        this.isPlaying = false;

        /* Hide the video player */
        this.videoPlayerWrapper.classList.remove("visible");

        /* Show overlay, play button, and thumbnail again */
        if (this.sectionWrap) {
          this.sectionWrap.classList.remove("video-is-playing");
        }

        /* Reset video to beginning */
        switch (this.videoType) {
          case "self-hosted": {
            const video = this.querySelector("video");
            if (video) {
              video.controls = false;
              video.pause();
              video.currentTime = 0;
            }
            break;
          }
          case "youtube":
            if (this.ytPlayer && this.ytPlayer.seekTo) {
              this.ytPlayer.pauseVideo();
              this.ytPlayer.seekTo(0);
            }
            break;
          case "vimeo":
            if (this.vimeoPlayer) {
              this.vimeoPlayer.pause();
              this.vimeoPlayer.setCurrentTime(0);
            }
            break;
        }
      }

      showPlayer() {
        this.videoPlayerWrapper.classList.add("visible");
        this.videoLoaderElement.classList.add("is-hidden");
        this.setAttribute("data-loaded", "true");
      }

      static loadYouTube() {
        if (window.TFP_YOUTUBE_LOADED) return;
        window.TFP_YOUTUBE_LOADED = true;
        const tag = document.createElement("script");
        const [firstScriptTag] = document.getElementsByTagName("script");
        tag.src = "https://www.youtube.com/player_api";
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      static loadVimeo() {
        if (window.TFP_VIMEO_LOADED) return;
        window.TFP_VIMEO_LOADED = true;
        const tag = document.createElement("script");
        const [firstScriptTag] = document.getElementsByTagName("script");
        tag.src = "https://player.vimeo.com/api/player.js";
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    },
  );
}
